import React from 'react';
import type { CredentialResponse } from '@react-oauth/google';
import { GoogleLogin } from '@react-oauth/google';
import { Action, StandIn } from '@martincastroalvarez/anubis-ui';

type GoogleSignInButtonProps = {
  /** The visible word — the network's name, like the three buttons beside it. */
  label: string;
  /**
   * The locale handed to Google's own control. It sits at opacity 0 under our face, so this changes
   * nothing visible; it is passed because the widget is still a real, focusable control and its
   * accessible name should not be in a different language from the page around it.
   */
  locale?: string;
  onCredential: (idToken: string) => void;
  onError?: () => void;
};

/** Google draws its button 200–400px wide, whatever its frame is: ask for the face's own width. */
export function googleWidth(face: number): number {
  return Math.min(400, Math.max(200, Math.round(face)));
}

/**
 * Google's real sign-in control, worn as one of ours: anubis' `StandIn` draws an `Action` with the
 * Google mark, and lays Google's iframe over it at zero opacity so the click lands where Google
 * needs it — a programmatic `.click()` on a hidden widget does not work across browsers.
 *
 * Two things made it "not work" (owner, 2026-10-20), both fixed through `StandIn`:
 * - **Pressed too early.** The face drew at once and Google's frame a moment later; a press in
 *   between landed on nothing. `wait` shows a button skeleton until the frame has loaded.
 * - **A dead strip.** Google draws its button at the width it is given (it was a fixed 400, clamped
 *   by Google to 286–300) while the face spans the card (358px on a phone), so a press on the face's
 *   right part missed. The button is now asked for the face's measured width.
 */
export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  label,
  locale,
  onCredential,
  onError,
}) => {
  const handleSuccess = (cred: CredentialResponse) => {
    if (cred.credential) onCredential(cred.credential);
    else onError?.();
  };
  return (
    <StandIn
      wait
      face={
        <Action variant="ghost" block icon="google">
          {label}
        </Action>
      }
    >
      {(width) => (
        <GoogleLogin
          onSuccess={handleSuccess}
          {...(onError ? { onError } : {})}
          {...(locale ? { locale } : {})}
          useOneTap={false}
          theme="filled_black"
          shape="pill"
          text="signin_with"
          size="large"
          width={googleWidth(width)}
        />
      )}
    </StandIn>
  );
};
